import { translate } from '@utils/i18n';
import { formatFileSize } from '@features/Attachments/utils/fileFormatting';
import { visibilitySummary } from './visibility';
import { BUILDING_ATTACHMENTS_NS, PORTAL_VALUE, TECHS_VALUE, UPLOAD_NS, VISIBILITY_EDIT_NS } from '../constants';
/**
 * Finalize attribute keys. Snake_case because `directUploadTransport` posts through the raw axios
 * client with no case conversion, so these reach the wire verbatim — they are the Frozen
 * contract's own field names (contracts/direct-uploads-office.md) and exactly what
 * `Api::Internal::Buildings::AttachmentUploadsController#permitted_attributes` allows.
 */
const ATTR_CATEGORY = 'attachment_category_id';
const ATTR_IS_PRIVATE = 'is_private';
const ATTR_SHOW_ON_PORTAL = 'show_on_customer_portal';
/** Drawer column keys — the `fields` keys the queue rows read their values from. */
const COLUMN_CATEGORY = 'category';
const COLUMN_VISIBILITY = 'visibility';
/**
 * Module-level constant, not an inline literal: it feeds `useDirectUpload`'s `addFiles`
 * dependency, so a fresh object per render would rebuild that callback every time.
 */
export const BUILDING_DEFAULT_UPLOAD_ATTRIBUTES = {
    [ATTR_CATEGORY]: null,
    [ATTR_IS_PRIVATE]: false,
    [ATTR_SHOW_ON_PORTAL]: false
};
/** Projects a staged row's finalize attributes onto the two drawer columns' display values. */
export function toBuildingQueueFields(item) {
    var _a;
    const isPrivate = Boolean(item.attributes[ATTR_IS_PRIVATE]);
    const showOnCustomerPortal = Boolean(item.attributes[ATTR_SHOW_ON_PORTAL]);
    return {
        [COLUMN_CATEGORY]: ((_a = item.attributes[ATTR_CATEGORY]) !== null && _a !== void 0 ? _a : null),
        [COLUMN_VISIBILITY]: [...(!isPrivate ? [TECHS_VALUE] : []), ...(showOnCustomerPortal ? [PORTAL_VALUE] : [])]
    };
}
/** All `AddAttachmentsBrushaway` copy -- this organism takes `labels` and never imports
 *  `translate` itself (`@ui` stays copy-free). */
export function buildAddAttachmentsLabels(maxSizeBytes) {
    const sizeLimitLabel = formatFileSize(maxSizeBytes);
    const NS = UPLOAD_NS;
    return {
        title: translate('title', { namespace: NS }),
        columnName: translate('column_name', { namespace: NS }),
        columnType: translate('column_type', { namespace: NS }),
        retryActionLabel: translate('retry_action', { namespace: NS }),
        removeActionLabel: (fileName) => translate('remove_action', { namespace: NS, fileName }),
        metaSize: (size) => translate('meta.size', { namespace: NS, size }),
        metaWaiting: (size) => translate('meta.waiting', { namespace: NS, size }),
        metaUploaded: (size) => translate('meta.uploaded', { namespace: NS, size }),
        metaFinalizing: (size) => translate('meta.finalizing', { namespace: NS, size }),
        footerTotals: (count, size) => translate('footer_totals', { namespace: NS, count, size }),
        cancelActionLabel: translate('cancel_action', { namespace: NS }),
        metaFailed: (size) => translate('meta.failed', { namespace: NS, size }),
        addButtonLabel: (count) => translate('add_button', { namespace: NS, count }),
        uploadingSummary: (done, total, pct) => translate('uploading_summary', { namespace: NS, done, total, pct }),
        uploadedSummary: (done, total, failed) => translate('uploaded_summary', { namespace: NS, done, total, failed }),
        cancelUploadLabel: translate('cancel_upload', { namespace: NS }),
        retryFailedLabel: translate('retry_failed', { namespace: NS }),
        rejectedAlertTitle: (count) => translate('rejected_alert.title', { namespace: NS, count }),
        rejectedAlertMessage: (fileName, size) => translate('rejected_alert.message', { namespace: NS, fileName, size, limit: sizeLimitLabel }),
        rejectedAlertTypeMessage: (fileName) => translate('rejected_alert.type_message', { namespace: NS, fileName }),
        dropzoneNote: translate('dropzone_note', { namespace: NS, limit: sizeLimitLabel }),
        removeConfirmTitle: translate('remove_confirm.title', { namespace: NS }),
        removeConfirmDescription: (fileName) => translate('remove_confirm.description', { namespace: NS, fileName }),
        removeConfirmConfirmLabel: translate('remove_confirm.confirm_label', { namespace: NS }),
        removeConfirmCancelLabel: translate('remove_confirm.cancel_label', { namespace: NS }),
        discardConfirmTitle: translate('discard_confirm.title', { namespace: NS }),
        discardConfirmDescription: translate('discard_confirm.description', { namespace: NS }),
        discardConfirmConfirmLabel: translate('discard_confirm.confirm_label', { namespace: NS }),
        discardConfirmCancelLabel: translate('discard_confirm.cancel_label', { namespace: NS }),
        closeLabel: translate('close_label', { namespace: NS })
    };
}
/**
 * Building's two drawer columns. Each `onChange` writes one finalize attribute, which is why
 * `updateItemMetadata` merges rather than replaces — setting a category must not clear the
 * visibility the user already set on that row.
 *
 * Reuses the edit modal's existing `techs_label`/`portal_label` keys and `visibilitySummary`
 * rather than duplicating them under `upload.*`.
 */
export function buildAddAttachmentsColumns(categoryOptions, showPortalVisibilityOption, updateItemMetadata, addCategory) {
    return [
        {
            kind: 'select',
            key: COLUMN_CATEGORY,
            label: translate('column_category', { namespace: UPLOAD_NS }),
            width: '180px',
            options: categoryOptions.map((option) => ({ value: option.id, label: option.label })),
            onChange: (id, value) => updateItemMetadata(id, { attributes: { [ATTR_CATEGORY]: value } }),
            footerActionLabel: addCategory === null || addCategory === void 0 ? void 0 : addCategory.label,
            onRequestFooterAction: addCategory
                ? (apply) => addCategory.onRequest((categoryId) => apply(categoryId))
                : undefined
        },
        {
            kind: 'flags',
            key: COLUMN_VISIBILITY,
            label: translate('column_visible_to', { namespace: UPLOAD_NS }),
            width: '180px',
            options: [
                { value: TECHS_VALUE, label: translate('techs_label', { namespace: VISIBILITY_EDIT_NS }) },
                ...(showPortalVisibilityOption
                    ? [{ value: PORTAL_VALUE, label: translate('portal_label', { namespace: VISIBILITY_EDIT_NS }) }]
                    : [])
            ],
            getSummary: (values) => visibilitySummary(!values.includes(TECHS_VALUE), values.includes(PORTAL_VALUE), (key) => translate(key, { namespace: BUILDING_ATTACHMENTS_NS })),
            onChange: (id, values) => updateItemMetadata(id, {
                attributes: {
                    [ATTR_IS_PRIVATE]: !values.includes(TECHS_VALUE),
                    [ATTR_SHOW_ON_PORTAL]: values.includes(PORTAL_VALUE)
                }
            })
        }
    ];
}
